(() => {
  const heroCleanups = new Map();
  function initialize(root) {
    root.querySelectorAll('[data-v6-format]').forEach(card => {
      if (card.dataset.v6FormatEnhanced) return;
      const reveal = card.querySelector('[data-v6-format-reveal]');
      const panel = card.querySelector('[data-v6-format-panel]');
      if (!reveal || !panel) return;
      const visibleLayout = window.matchMedia('(max-width: 749px), (hover: none)');
      let pointerInside = false;
      card.dataset.v6FormatEnhanced = 'true';
      function show(open) {
        const expanded = visibleLayout.matches || open;
        card.classList.toggle('is-revealed', expanded);
        reveal.setAttribute('aria-expanded', String(expanded));
        panel.inert = !expanded;
      }
      function updateLayout() {
        reveal.hidden = visibleLayout.matches;
        show(card.contains(document.activeElement));
      }
      reveal.addEventListener('click', () => show(true));
      card.addEventListener('pointerenter', event => {
        if (event.pointerType === 'touch') return;
        pointerInside = true;
        show(true);
      });
      card.addEventListener('pointerleave', () => {
        pointerInside = false;
        show(card.contains(document.activeElement));
      });
      card.addEventListener('focusin', () => show(true));
      card.addEventListener('focusout', event => {
        if (!card.contains(event.relatedTarget)) show(pointerInside);
      });
      card.addEventListener('keydown', event => {
        if (event.key !== 'Escape' || visibleLayout.matches) return;
        event.preventDefault();
        reveal.focus({ preventScroll: true });
        pointerInside = false;
        show(false);
      });
      card.addEventListener('shopify:block:select', () => show(true));
      card.addEventListener('shopify:block:deselect', () => show(false));
      visibleLayout.addEventListener('change', updateLayout);
      updateLayout();
    });
    root.querySelectorAll('[data-v6-hero]').forEach(hero => {
      if (hero.dataset.initialized) return;
      hero.dataset.initialized = 'true';
      const slides = [...hero.querySelectorAll('[data-v6-slide]')];
      const buttons = [...hero.querySelectorAll('[data-v6-slide-button]')];
      const rotation = hero.querySelector('[data-v6-rotation]');
      if (slides.length < 2 || !rotation) return;
      const controller = new AbortController();
      const { signal } = controller;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      let active = Math.max(0, slides.findIndex(slide => !slide.hidden));
      let paused = reducedMotion.matches;
      let hovered = false;
      let focused = hero.contains(document.activeElement);
      let visible = false;
      let editorSelected = false;
      let timer;
      let preloadTimer;
      let gesture;
      let suppressClickUntil = 0;
      const listen = (target, event, handler, options = {}) => target.addEventListener(event, handler, { ...options, signal });
      const canRotate = () => !paused && !hovered && !focused && visible && !document.hidden && !editorSelected && !gesture;
      function schedule() {
        window.clearTimeout(timer);
        if (canRotate()) timer = window.setTimeout(() => show(active + 1), 7000);
      }
      function updateRotation() {
        rotation.setAttribute('aria-label', paused ? rotation.dataset.playLabel : rotation.dataset.pauseLabel);
        rotation.setAttribute('title', paused ? rotation.dataset.playLabel : rotation.dataset.pauseLabel);
        rotation.querySelector('[data-v6-play-icon]').toggleAttribute('hidden', !paused);
        rotation.querySelector('[data-v6-pause-icon]').toggleAttribute('hidden', paused);
        hero.dataset.v6Rotation = paused ? 'paused' : 'playing';
        schedule();
      }
      function show(index) {
        active = (index + slides.length) % slides.length;
        slides.forEach((slide, i) => { slide.hidden = i !== active; });
        buttons.forEach((button, i) => button.setAttribute('aria-pressed', String(i === active)));
        schedule();
      }
      function stopGesture() {
        const pointerId = gesture?.id;
        gesture = undefined;
        if (pointerId !== undefined && hero.hasPointerCapture(pointerId)) hero.releasePointerCapture(pointerId);
        schedule();
      }
      buttons.forEach((button, index) => {
        listen(button, 'click', () => show(index));
        listen(button, 'keydown', event => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          const next = (index + (event.key === 'ArrowRight' ? 1 : -1) + slides.length) % slides.length;
          show(next);
          buttons[next].focus({ preventScroll: true });
        });
      });
      listen(rotation, 'click', () => { paused = !paused; updateRotation(); });
      listen(hero, 'pointerenter', event => {
        if (event.pointerType !== 'mouse') return;
        hovered = true;
        schedule();
      });
      listen(hero, 'pointerleave', event => {
        if (event.pointerType !== 'mouse') return;
        hovered = false;
        schedule();
      });
      listen(hero, 'focusin', () => { focused = true; schedule(); });
      listen(hero, 'focusout', event => { focused = hero.contains(event.relatedTarget); schedule(); });
      listen(document, 'visibilitychange', schedule);
      listen(reducedMotion, 'change', event => {
        if (event.matches) paused = true;
        updateRotation();
      });
      listen(hero, 'pointerdown', event => {
        if (event.pointerType !== 'touch') return;
        if (!event.isPrimary) { stopGesture(); return; }
        if (event.target.closest('a, button, input, select, textarea, summary, [contenteditable]')) return;
        gesture = { id: event.pointerId, x: event.clientX, y: event.clientY };
        hero.setPointerCapture(event.pointerId);
        schedule();
      });
      listen(hero, 'pointerup', event => {
        if (!gesture || event.pointerId !== gesture.id) return;
        const dx = event.clientX - gesture.x;
        const dy = event.clientY - gesture.y;
        const shouldSwitch = Math.abs(dx) >= 50 && Math.abs(dx) > Math.abs(dy) * 1.35;
        stopGesture();
        if (shouldSwitch) {
          suppressClickUntil = performance.now() + 500;
          show(active + (dx < 0 ? 1 : -1));
        }
      });
      listen(hero, 'pointercancel', stopGesture);
      listen(hero, 'lostpointercapture', () => { if (gesture) stopGesture(); });
      listen(hero, 'click', event => {
        if (performance.now() >= suppressClickUntil || event.target.closest('button')) return;
        event.preventDefault();
        event.stopPropagation();
      }, { capture: true });
      listen(hero, 'shopify:block:select', event => {
        editorSelected = true;
        const index = slides.findIndex(slide => slide === event.target || slide.contains(event.target));
        if (index >= 0) show(index);
        schedule();
      });
      listen(hero, 'shopify:block:deselect', () => { editorSelected = false; schedule(); });
      const observer = new IntersectionObserver(entries => {
        visible = entries.some(entry => entry.isIntersecting);
        schedule();
      });
      observer.observe(hero);
      function preloadNext() {
        preloadTimer = window.setTimeout(() => {
          const nextImage = slides[(active + 1) % slides.length].querySelector('img');
          if (nextImage && visible && !window.matchMedia('(prefers-reduced-motion: reduce)').matches && !navigator.connection?.saveData && !/2g/.test(navigator.connection?.effectiveType || '')) nextImage.loading = 'eager';
        }, 3500);
      }
      const firstImage = slides[active].querySelector('img');
      if (!firstImage || firstImage.complete) preloadNext();
      else {
        listen(firstImage, 'load', preloadNext, { once: true });
        listen(firstImage, 'error', preloadNext, { once: true });
      }
      rotation.hidden = false;
      updateRotation();
      heroCleanups.set(hero, () => {
        controller.abort();
        observer.disconnect();
        window.clearTimeout(timer);
        window.clearTimeout(preloadTimer);
        delete hero.dataset.initialized;
        rotation.hidden = true;
      });
    });
  }
  initialize(document);
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    heroCleanups.forEach((cleanup, hero) => {
      if (event.target !== hero && !event.target.contains(hero)) return;
      cleanup();
      heroCleanups.delete(hero);
    });
  });
})();
